export type PredictionDomainIcon =
  | "sports"
  | "audience"
  | "policy"
  | "product"
  | "market"
  | "story";

export type PredictionWorkflowIcon = "evidence" | "world" | "forecast";

export type PredictionOption = {
  value: string;
  label: string;
};

export type PredictionLens = PredictionOption & {
  description: string;
  prompt: string;
};

export type PredictionPageConfig = {
  version: 1;
  key: string;
  path: `/${string}`;
  analyticsPrefix: string;
  testIdPrefix: string;
  breadcrumbLabel: string;
  domainIcon: PredictionDomainIcon;
  seo: {
    title: string;
    description: string;
    keywords: string[];
    applicationName: string;
    applicationCategory: string;
    featureList: string[];
  };
  hero: {
    eyebrow: string;
    title: string;
    description: string;
    primaryCta: string;
    secondaryCta: string;
    boundary: string;
    imageSrc: string;
    imageAlt: string;
    imageObjectPosition: string;
    viewLabel: string;
    scopeFallback: string;
    scenarioLabel: string;
    subjectAFallback: string;
    subjectBFallback: string;
    subjectConnector: string;
    signalLabel: string;
    provenanceLabel: string;
  };
  facts: {
    items: Array<{ value: string; label: string }>;
    note: string;
    sources: Array<{ label: string; href: string }>;
  };
  lab: {
    eyebrow: string;
    title: string;
    description: string;
    setupEyebrow: string;
    setupTitle: string;
    scopeLabel: string;
    defaultScope: string;
    scopes: PredictionOption[];
    subjectA: { label: string; placeholder: string };
    subjectB: { label: string; placeholder: string };
    duplicateSubjectsError: string;
    lensLabel: string;
    defaultLens: string;
    lenses: PredictionLens[];
    contextLabel: string;
    contextPlaceholder: string;
    evidenceEyebrow: string;
    evidenceTitle: string;
    dropTitle: string;
    publicLinkLabel: string;
    publicLinkPlaceholder: string;
    publicLinkHelp: string;
    preparingUploadLabel: string;
    preparingLinkLabel: string;
    launchLabel: string;
    outputDisclaimer: string;
  };
  report: {
    eyebrow: string;
    title: string;
    description: string;
    outputs: Array<{ title: string; description: string }>;
  };
  workflow: {
    eyebrow: string;
    title: string;
    steps: Array<{
      icon: PredictionWorkflowIcon;
      title: string;
      description: string;
    }>;
    boundaryTitle: string;
    boundaryDescription: string;
  };
  faq: {
    eyebrow: string;
    title: string;
    items: Array<{ question: string; answer: string }>;
    footnote: string;
  };
  prompt: {
    subjectAFallback: string;
    subjectBFallback: string;
    introduction: string;
    evidenceInstruction: string;
    outputInstruction: string;
    boundaryInstruction: string;
    contextPrefix: string;
  };
};

export type PredictionPromptInput = {
  scope: string;
  subjectA: string;
  subjectB: string;
  lens: string;
  context?: string;
};

const replacePromptTokens = (
  template: string,
  values: Record<string, string>
) =>
  template.replace(
    /\{\{([a-zA-Z]+)\}\}/g,
    (_, key: string) => values[key] ?? ""
  );

export const buildPredictionPrompt = (
  config: PredictionPageConfig,
  input: PredictionPromptInput
) => {
  const scope =
    config.lab.scopes.find((item) => item.value === input.scope)?.label ??
    config.hero.scopeFallback;
  const subjectA = input.subjectA.trim() || config.prompt.subjectAFallback;
  const subjectB = input.subjectB.trim() || config.prompt.subjectBFallback;
  const lens =
    config.lab.lenses.find((item) => item.value === input.lens)?.prompt ??
    config.lab.lenses[0]?.prompt ??
    "the most plausible scenario path";
  const context = input.context?.trim();
  const tokens = { scope, subjectA, subjectB, lens };

  return [
    replacePromptTokens(config.prompt.introduction, tokens),
    replacePromptTokens(config.prompt.evidenceInstruction, tokens),
    config.prompt.outputInstruction,
    config.prompt.boundaryInstruction,
    context ? `${config.prompt.contextPrefix} ${context}` : "",
  ]
    .filter(Boolean)
    .join(" ");
};
